import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { collection, query, where, getDocs, getDoc, updateDoc, doc } from 'firebase/firestore'
import { db } from '../utils/firebase'
import {
  Home,
  Ticket,
  AlertCircle,
  Loader,
  CheckCircle,
  MapPin,
  Mail,
  LogOut,
  ArrowRight,
  ArrowLeft,
  Users
} from 'lucide-react'
import './NoNeighborhoodPage.css'

function NoNeighborhoodPage() {
  const { userProfile, currentUser, logout, updateUserNeighborhood } = useAuth()
  const [step, setStep] = useState('welcome') // 'welcome', 'join', 'invite-admin'
  const [invitationCode, setInvitationCode] = useState('')
  const [invitationData, setInvitationData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const validateInvitationCode = async (e) => {
    e.preventDefault()

    if (!invitationCode.trim()) {
      setError('Please enter an invitation code')
      return
    }

    try {
      setError('')
      setLoading(true)

      // Query for the invitation code
      const invitationsRef = collection(db, 'invitations')
      const q = query(invitationsRef, where('code', '==', invitationCode.toUpperCase().trim()))
      const querySnapshot = await getDocs(q)

      if (querySnapshot.empty) {
        setError('Invalid invitation code. Please check and try again.')
        return
      }

      const inviteDoc = querySnapshot.docs[0]
      const inviteData = inviteDoc.data()

      // Check if invitation is already used
      if (inviteData.isUsed) {
        setError('This invitation code has already been used.')
        return
      }

      // (Invitation codes do not expire. Older invitation docs may carry an
      //  `expirationDate` field set by the legacy iOS code path that defaulted
      //  to 6 months out — we intentionally ignore it. Codes are only
      //  invalidated when they're used, regenerated, or admin-vacated.)

      // Fetch neighborhood name
      let neighborhoodName = inviteData.neighborhoodId
      try {
        const neighborhoodDoc = await getDoc(doc(db, "neighborhoods", inviteData.neighborhoodId))
        if (neighborhoodDoc.exists()) {
          neighborhoodName = neighborhoodDoc.data().name || inviteData.neighborhoodId
        }
      } catch (e) {
        console.error("Error fetching neighborhood:", e)
      }

      // Store invitation data for confirmation
      setInvitationData({
        id: inviteDoc.id,
        ...inviteData,
        neighborhoodName
      })

    } catch (err) {
      console.error('Error validating invitation:', err)
      setError('Failed to validate invitation code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const joinNeighborhood = async () => {
    if (!invitationData) return

    try {
      setLoading(true)
      setError('')

      // Update user's neighborhood
      await updateUserNeighborhood(
        invitationData.neighborhoodId,
        invitationData.address
      )

      // Mark invitation as used
      await updateDoc(doc(db, 'invitations', invitationData.id), {
        isUsed: true,
        usedAt: new Date(),
        usedBy: currentUser?.email?.toLowerCase()
      })

      // Update address status if addressId exists
      if (invitationData.addressId) {
        try {
          await updateDoc(doc(db, 'neighborhoods', invitationData.neighborhoodId, 'addresses', invitationData.addressId), {
            status: 'active',
            residentUserId: currentUser?.email?.toLowerCase(),
            residentName: userProfile?.name,
            residentEmail: currentUser?.email?.toLowerCase()
          })
        } catch (addrErr) {
          console.error('Error updating address status:', addrErr)
        }
      }

      setSuccess(true)

      // Reload the page after a brief delay to show success
      setTimeout(() => {
        window.location.reload()
      }, 1500)

    } catch (err) {
      console.error('Error joining neighborhood:', err)
      setError('Failed to join neighborhood. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (err) {
      console.error('Logout error:', err)
    }
  }

  // Success state
  if (success) {
    return (
      <div className="no-neighborhood-page">
        <div className="success-card">
          <div className="success-icon">
            <CheckCircle size={48} />
          </div>
          <h2>Welcome to {invitationData?.neighborhoodName}!</h2>
          <p>You've successfully joined your new neighborhood.</p>
          <p className="redirecting">Redirecting you to the dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="no-neighborhood-page">
      <div className="no-neighborhood-container">
        {/* Header with user info and logout */}
        <div className="user-header">
          <div className="user-info">
            <span className="user-name">{userProfile?.name || userProfile?.firstName || 'User'}</span>
            <span className="user-email">{currentUser?.email}</span>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} />
            Sign Out
          </button>
        </div>

        {/* Welcome State */}
        {step === 'welcome' && !invitationData && (
          <div className="welcome-section">
            <div className="logo-area">
              <Home size={64} className="home-icon" />
              <h1>Welcome to NeighborTopia</h1>
            </div>

            <p className="subtitle">
              It looks like you're not currently part of a neighborhood community.
              This might be because you've moved or your previous community is no longer active.
            </p>

            <div className="options-grid">
              <div className="option-card" onClick={() => setStep('join')}>
                <div className="option-icon join">
                  <Ticket size={32} />
                </div>
                <h3>Join a Neighborhood</h3>
                <p>Have an invitation code from your HOA or neighborhood association? Enter it to join your new community.</p>
                <span className="option-cta">
                  Enter Code <ArrowRight size={16} />
                </span>
              </div>

              <div className="option-card" onClick={() => setStep('invite-admin')}>
                <div className="option-icon invite">
                  <Users size={32} />
                </div>
                <h3>Invite Your HOA or Neighborhood Association</h3>
                <p>Your community isn't on NeighborTopia yet? Invite your HOA or neighborhood association admin to get started.</p>
                <span className="option-cta">
                  Learn More <ArrowRight size={16} />
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Join Neighborhood State */}
        {step === 'join' && !invitationData && (
          <div className="join-section">
            <button className="back-btn" onClick={() => setStep('welcome')}>
              <ArrowLeft size={18} />
              Back
            </button>

            <div className="section-header">
              <Ticket size={48} className="section-icon" />
              <h2>Join a Neighborhood</h2>
              <p>Enter the 6-character invitation code from your HOA or neighborhood association welcome packet or email.</p>
            </div>

            <form onSubmit={validateInvitationCode} className="code-form">
              <div className="code-input-wrapper">
                <input
                  type="text"
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                  placeholder="XXXXXX"
                  maxLength={6}
                  className="code-input"
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <Loader size={18} className="spinner" />
                    Validating...
                  </>
                ) : (
                  'Validate Code'
                )}
              </button>
            </form>

            <p className="help-text">
              Don't have a code? Contact your HOA or neighborhood association administrator to request an invitation.
            </p>
          </div>
        )}

        {/* Confirm Join State */}
        {invitationData && (
          <div className="confirm-section">
            <button className="back-btn" onClick={() => { setInvitationData(null); setStep('join'); }}>
              <ArrowLeft size={18} />
              Back
            </button>

            <div className="section-header">
              <CheckCircle size={48} className="section-icon success" />
              <h2>Invitation Valid!</h2>
              <p>You're about to join:</p>
            </div>

            <div className="neighborhood-preview">
              <h3>{invitationData.neighborhoodName}</h3>
              <div className="address-info">
                <MapPin size={18} />
                <span>{invitationData.address}</span>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button
              className="submit-btn"
              onClick={joinNeighborhood}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={18} className="spinner" />
                  Joining...
                </>
              ) : (
                'Join This Neighborhood'
              )}
            </button>
          </div>
        )}

        {/* Invite Admin State */}
        {step === 'invite-admin' && (
          <div className="invite-admin-section">
            <button className="back-btn" onClick={() => setStep('welcome')}>
              <ArrowLeft size={18} />
              Back
            </button>

            <div className="section-header">
              <Mail size={48} className="section-icon" />
              <h2>Bring NeighborTopia to Your Community</h2>
              <p>Help your HOA or neighborhood association discover the benefits of a connected community.</p>
            </div>

            <div className="info-card">
              <h3>How to Get Started</h3>
              <ol>
                <li>Share NeighborTopia with your HOA board, neighborhood association board, or property manager</li>
                <li>They can sign up as an administrator at <strong>neighbortopia.app</strong></li>
                <li>Once set up, they'll send invitation codes to all residents</li>
                <li>Enter your code here to join your community!</li>
              </ol>
            </div>

            <div className="share-section">
              <p>Share this link with your HOA or neighborhood association administrator:</p>
              <div className="share-link">
                <input
                  type="text"
                  value="https://neighbortopia.app"
                  readOnly
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText('https://neighbortopia.app')
                    alert('Link copied to clipboard!')
                  }}
                >
                  Copy
                </button>
              </div>
            </div>

            <p className="help-text">
              Questions? Contact us at <a href="mailto:support@neighbortopia.app">support@neighbortopia.app</a>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default NoNeighborhoodPage
